// src/pages/auth/Signup.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, User, Mail, Lock, Check, Store } from 'lucide-react';
import { signupUser } from '../../services/api/auth';
import { getProviderByInviteHash } from '../../services/api/dropshipping';
import { useAuth } from '../../hooks/useAuth';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import LanguageSelector from '../../components/common/LanguageSelector/LanguageSelector';
import styles from './Signup.module.css';

const Signup = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLocalization();
  const { success } = useAlert();
  const [inviteHash, setInviteHash] = useState(null);
  const [inviteProviderName, setInviteProviderName] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const hash = localStorage.getItem('dropshippingInviteHash');
    if (hash) {
      setInviteHash(hash);
      getProviderByInviteHash(hash)
        .then((data) => setInviteProviderName(data.providerName || ''))
        .catch(() => {
          // Invalid hash — remove it silently
          localStorage.removeItem('dropshippingInviteHash');
          setInviteHash(null);
        });
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('auth.nameRequired');
    }

    if (!formData.email) {
      newErrors.email = t('forms.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('forms.invalidEmail');
    }

    if (!formData.password) {
      newErrors.password = t('forms.passwordRequired');
    } else if (formData.password.length < 6) {
      newErrors.password = t('auth.passwordMinLength');
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.confirmPassword');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.passwordsDoNotMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      };
      if (inviteHash) {
        payload.providerInviteHash = inviteHash;
      }
      const response = await signupUser(payload);

      // Check if auto-login is enabled (new format)
      if (response.autoLogin && response.token && response.user) {
        // Clear invite hash from localStorage after successful signup
        localStorage.removeItem('dropshippingInviteHash');

        // Store authentication data
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));

        // Update auth context for immediate login
        login(response.user, response.token);

        // Show success message for auto-login
        success(t('auth.accountCreatedAndLoggedIn', { name: response.user.name }), {
          title: t('auth.signupSuccessful'),
          duration: 4000
        });

        // Navigate to dashboard (setup wizard will show if needed)
        navigate('/dashboard');
      } else {
        // Fallback to old behavior - redirect to login
        success(t('auth.accountCreated'), {
          title: t('auth.signupSuccessful'),
          duration: 4000
        });
        navigate('/login');
      }
    } catch (error) {
      if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      } else {
        setErrors({ submit: t('errors.somethingWentWrong') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // Implement Google signup logic here
    console.log('Google signup clicked');
  };

  return (
    <div className={styles.signupContainer}>
      <div className={styles.signupCard}>
        {/* Language Selector */}
        <LanguageSelector variant="auth" />

        <div className={styles.signupHeader}>
          <h1>{t('auth.createAccount')}</h1>
          <p>{t('auth.startManaging')}</p>
        </div>

        {inviteHash && inviteProviderName && (
          <div className={styles.inviteBanner}>
            <Store size={18} />
            <span>{t('dropshipping.joinAsReseller')} <strong>{inviteProviderName}</strong></span>
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.signupForm}>
          {errors.submit && (
            <div className={styles.errorBanner}>
              {errors.submit}
            </div>
          )}

          <div className={styles.formGroup}>
            <label htmlFor="name">{t('auth.name')}</label>
            <div className={styles.inputWrapper}>
              <User className={styles.inputIcon} size={20} />
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t('auth.name')}
                className={errors.name ? styles.error : ''}
              />
            </div>
            {errors.name && <span className={styles.errorText}>{errors.name}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email">{t('auth.email')}</label>
            <div className={styles.inputWrapper}>
              <Mail className={styles.inputIcon} size={20} />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('auth.email')}
                className={errors.email ? styles.error : ''}
              />
            </div>
            {errors.email && <span className={styles.errorText}>{errors.email}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password">{t('auth.password')}</label>
            <div className={styles.inputWrapper}>
              <Lock className={styles.inputIcon} size={20} />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('auth.password')}
                className={errors.password ? styles.error : ''}
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && <span className={styles.errorText}>{errors.password}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
            <div className={styles.inputWrapper}>
              <Lock className={styles.inputIcon} size={20} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('auth.confirmPassword')}
                className={errors.confirmPassword ? styles.error : ''}
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.confirmPassword && <span className={styles.errorText}>{errors.confirmPassword}</span>}
          </div>

          <button 
            type="submit" 
            className={styles.signupButton}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className={styles.loadingSpinner}></div>
            ) : (
              <>
                <Check size={20} />
                {t('auth.createAccount')}
              </>
            )}
          </button>
        </form>

        {/* Google login will be implemented in future */}
        {/* <div className={styles.divider}>
          <span>{t('common.or')}</span>
        </div>

        <button 
          type="button" 
          className={styles.googleButton}
          onClick={handleGoogleSignup}
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {t('auth.continueWithGoogle')}
        </button> */}

        <div className={styles.signupFooter}>
          <p>
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className={styles.loginLink}>
              {t('auth.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;